import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import {
  GET_BLUEPRINT_TASKABLE_TYPES_QUERY_KEY,
  GET_BLUEPRINTS_QUERY_KEY
} from '@/constants/blueprints';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import BlueprintRepository from '@/repositories/BlueprintRepository';
import WorkRepository from '@/repositories/WorkRepository';
import type { Blueprint, BlueprintPayload } from '@/types/blueprint';
import { QueryParams } from '@/types/query';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const useBlueprint = (blueprintId: string | undefined) => {
  const workRepo = WorkRepository();

  return useAPI({
    queryKey: ['getBlueprint', blueprintId],
    queryFn: async () => {
      return await workRepo.getBlueprint(blueprintId as string);
    },
    enabled: !!blueprintId
  });
};

export const useUpdateBlueprintFormSchema = (blueprintId: string) => {
  const workRepo = WorkRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formSchema: Blueprint['form_schema']) => {
      try {
        const response = await workRepo.updateBlueprintFormSchema(blueprintId, formSchema);
        if (response.isError) {
          throw new Error(response.errorMessage ?? 'Failed to update blueprint');
        }
        return response.data;
      } catch (error) {
        // defaultRepositoryError rejects with a plain string; normalise so
        // callers (and react-query) always receive an Error with a message.
        if (error instanceof Error) throw error;
        throw new Error(typeof error === 'string' ? error : 'Failed to update blueprint');
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['getBlueprint', blueprintId] });
    }
  });
};

export const useBlueprints = (params: QueryParams) => {
  const blueprintRepository = BlueprintRepository();

  return useAPI({
    queryKey: [GET_BLUEPRINTS_QUERY_KEY, params],
    queryFn: async () => await blueprintRepository.list(params)
  });
};

export const useTaskableTypes = () => {
  const blueprintRepository = BlueprintRepository();

  return useAPI({
    queryKey: [GET_BLUEPRINT_TASKABLE_TYPES_QUERY_KEY],
    queryFn: async () => await blueprintRepository.listTaskableTypes()
  });
};

export const useCreateBlueprint = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const blueprintRepository = BlueprintRepository();

  return useMutation({
    mutationFn: (data: Partial<BlueprintPayload>) => blueprintRepository.create(data),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Blueprint created successfully' });
      await queryClient.invalidateQueries({ queryKey: [GET_BLUEPRINTS_QUERY_KEY] });
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export const useUpdateBlueprint = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const blueprintRepository = BlueprintRepository();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BlueprintPayload> }) =>
      blueprintRepository.update(id, data),
    onSuccess: async (_data, { id }) => {
      addMessage({ level: 'success', text: 'Blueprint updated successfully' });
      await queryClient.invalidateQueries({ queryKey: [GET_BLUEPRINTS_QUERY_KEY] });
      await queryClient.invalidateQueries({ queryKey: ['getBlueprint', id] });
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export const useDeleteBlueprint = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const blueprintRepository = BlueprintRepository();

  return useMutation({
    mutationFn: (id: string) => blueprintRepository.delete(id),
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Blueprint deleted successfully' });
      await queryClient.invalidateQueries({ queryKey: [GET_BLUEPRINTS_QUERY_KEY] });
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export default useBlueprint;
